import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute, Link, useMatch } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import type { FunctionReturnType } from 'convex/server';
import { useQuery } from 'convex-helpers/react';
import { differenceInMinutes, format, formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import { DropdownMenuItemDestructive } from '@/components/DropdownMenuItemDestructive';
import { HeartbeatVisualization } from '@/components/HeartbeatVisualization';
import { SuperAdminOnly } from '@/components/SuperAdminOnly';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ActionButton } from '@/components/ui+/ActionButton';
import { ColorBadge } from '@/components/ui+/ColorBadge';
import { CopyTextButton } from '@/components/ui+/CopyTextButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { AddIPadDialog } from './components/AddIPadDialog';
import { CashDrawerSettings } from './components/CashDrawerSettings';
import { RenameIPadDialog } from './components/RenameIPadDialog';
import { UnlinkIPadDialog } from './components/UnlinkIPadDialog';
import { formatDeviceCode } from './formatDeviceCode';

export const Route = createFileRoute('/r/$restaurantId/s/ipads/')({
  component: Component,
});

type IPad = FunctionReturnType<typeof api.iPads.listDevices>['devices'][number];

function Component() {
  const { restaurantId } = Route.useParams();
  const match = useMatch({ strict: false }); // Get the current route match
  const convex = useConvex();
  const { data, isPending } = useQuery(api.iPads.listDevices, { restaurantId });
  const { data: posConfig } = useQuery(api.posConfigs.getByRestaurantId, { restaurantId });
  const [isAdding, setIsAdding] = useState(false);
  const [iPadToRename, setIPadToRename] = useState<IPad | null>(null);
  const [iPadToUnlink, setIPadToUnlink] = useState<IPad | null>(null);

  if (isPending) return <LoadingIcon />;

  const iPads = data?.devices ?? [];
  const deviceCodes = data?.deviceCodes ?? [];

  // The USB printer plugged into an iPad points at it via `attachedToIPadUuid`.
  const attachedPrinterName = (iPadUuid: string) =>
    posConfig?.posPrinters.find((p) => p.attachedToIPadUuid === iPadUuid)?.name ?? '-';

  // Remote commands target a device by its `uuid`.
  const handleCheckForJsUpdate = async (posTerminalUuid: string) => {
    try {
      await convex.mutation(api.remoteCommands.issueCommand, {
        restaurantId,
        targetPosTerminalUuid: posTerminalUuid,
        commandType: 'CHECK_FOR_JS_UPDATE',
      });
      toast.success('JS update check command sent successfully');
    } catch (error) {
      toast.error("Couldn't send command", { description: getUserErrorMessage(error) });
    }
  };

  const handleReloadPos = async (posTerminalUuid: string) => {
    try {
      await convex.mutation(api.remoteCommands.issueCommand, {
        restaurantId,
        targetPosTerminalUuid: posTerminalUuid,
        commandType: 'RELOAD_POS',
      });
      toast.success('Reload POS command sent successfully');
    } catch (error) {
      toast.error("Couldn't send command", { description: getUserErrorMessage(error) });
    }
  };

  const handleClearLocalStorage = async (posTerminalUuid: string) => {
    try {
      await convex.mutation(api.remoteCommands.issueCommand, {
        restaurantId,
        targetPosTerminalUuid: posTerminalUuid,
        commandType: 'CLEAR_LOCAL_STORAGE',
      });
      toast.success('Clear local storage command sent successfully');
    } catch (error) {
      toast.error("Couldn't send command", { description: getUserErrorMessage(error) });
    }
  };

  const handleGetIPadTokenHash = async (posTerminalUuid: string) => {
    try {
      await convex.mutation(api.remoteCommands.issueCommand, {
        restaurantId,
        targetPosTerminalUuid: posTerminalUuid,
        commandType: 'GET_IPAD_TOKEN_HASH',
      });
      toast.success('Get iPad token hash command sent successfully');
    } catch (error) {
      toast.error("Couldn't send command", { description: getUserErrorMessage(error) });
    }
  };

  const handleGetActiveScreen = async (posTerminalUuid: string) => {
    try {
      await convex.mutation(api.remoteCommands.issueCommand, {
        restaurantId,
        targetPosTerminalUuid: posTerminalUuid,
        commandType: 'GET_ACTIVE_SCREEN',
      });
      toast.success('Get active screen command sent successfully');
    } catch (error) {
      toast.error("Couldn't send command", { description: getUserErrorMessage(error) });
    }
  };

  return (
    <>
      {isAdding && <AddIPadDialog onClose={() => setIsAdding(false)} restaurantId={restaurantId} />}
      {iPadToRename && (
        <RenameIPadDialog
          iPad={iPadToRename}
          onClose={() => setIPadToRename(null)}
          restaurantId={restaurantId}
        />
      )}
      {iPadToUnlink && (
        <UnlinkIPadDialog
          iPad={iPadToUnlink}
          onClose={() => setIPadToUnlink(null)}
          restaurantId={restaurantId}
        />
      )}

      {deviceCodes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active device codes</CardTitle>
            <CardDescription>
              Read these out to staff to sign in to the “OrderHub POS” iPad app. Each code works
              once.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deviceCodes.map((dc) => (
                  <TableRow key={dc._id}>
                    <TableCell className="font-mono font-medium tracking-widest">
                      <span className="inline-flex items-center gap-1">
                        {formatDeviceCode(dc.code)}
                        <CopyTextButton text={dc.code} />
                      </span>
                    </TableCell>
                    <TableCell>{dc.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>iPads</CardTitle>
          <CardDescription>
            iPads linked to this venue. Generate a code to add one, or unlink an iPad to sign it
            out.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {iPads.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Printer</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {iPads.map((iPad) => {
                  const mostRecentHeartbeat: number | null =
                    iPad.heartbeats.length > 0 ? Math.max(...iPad.heartbeats) : null;

                  const isOnlineNow =
                    !!mostRecentHeartbeat &&
                    differenceInMinutes(Date.now(), mostRecentHeartbeat) < 2;

                  return (
                    <TableRow key={iPad._id}>
                      <TableCell>
                        <div className="flex gap-2">
                          <HeartbeatVisualization
                            heartbeats={iPad.heartbeats}
                            isOnlineNow={isOnlineNow}
                          />

                          {isOnlineNow && <ColorBadge color="green">ONLINE</ColorBadge>}
                          {!isOnlineNow && !mostRecentHeartbeat && (
                            <ColorBadge color="gray">OFFLINE</ColorBadge>
                          )}
                          {!isOnlineNow && mostRecentHeartbeat && (
                            <Tooltip>
                              <TooltipTrigger>
                                <ColorBadge color="gray">OFFLINE</ColorBadge>
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                {`${formatDistanceToNow(mostRecentHeartbeat, { addSuffix: true })}, ${format(mostRecentHeartbeat, 'h:mm:ss a')}`}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{iPad.name ?? 'Unnamed iPad'}</TableCell>
                      <TableCell>{attachedPrinterName(iPad.uuid)}</TableCell>
                      <TableCell className="font-medium">
                        {iPad.appNativeBuildVersion}/{iPad.appJsBuildVersion}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {formatDistanceToNow(iPad.updatedAt, { addSuffix: true })}
                        </div>
                        <div className="text-muted-foreground">
                          {format(iPad.updatedAt, 'h:mm a d-MMM-yy')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <ActionDropdown>
                          <SuperAdminOnly>
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>Commands</DropdownMenuSubTrigger>
                              <DropdownMenuPortal>
                                <DropdownMenuSubContent>
                                  <DropdownMenuItem
                                    onSelect={() => handleCheckForJsUpdate(iPad.uuid)}
                                  >
                                    Check for JS update
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onSelect={() => handleReloadPos(iPad.uuid)}>
                                    Reload POS
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={() => handleClearLocalStorage(iPad.uuid)}
                                  >
                                    Clear local storage
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={() => handleGetIPadTokenHash(iPad.uuid)}
                                  >
                                    Get iPad token hash
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={() => handleGetActiveScreen(iPad.uuid)}
                                  >
                                    Get active screen
                                  </DropdownMenuItem>
                                </DropdownMenuSubContent>
                              </DropdownMenuPortal>
                            </DropdownMenuSub>
                          </SuperAdminOnly>

                          <DropdownMenuItem asChild>
                            <Link
                              to={'$deviceId'}
                              params={{ restaurantId, deviceId: iPad._id }}
                              from={match.fullPath}
                            >
                              View
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setIPadToRename(iPad)}>
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItemDestructive onSelect={() => setIPadToUnlink(iPad)}>
                            Unlink iPad
                          </DropdownMenuItemDestructive>
                        </ActionDropdown>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <ActionButton className="mt-4" onClick={() => setIsAdding(true)} variant="outline">
            Add iPad
          </ActionButton>
        </CardContent>
      </Card>

      {posConfig && (
        <CashDrawerSettings
          openCashDrawerTriggers={posConfig.openCashDrawerTriggers}
          restaurantId={restaurantId}
        />
      )}
    </>
  );
}
